import { ApiError, NetworkError, isApiErrorEnvelope } from './errors';
import { authStore, type TokenPair } from '../auth/store';

// HTTP-клиент платформы Starling на нативном fetch (общий для Light и Vision,
// V1.2-a; до этого жил в `frontend/light/src/api/client.ts`).
//
// Ответственность:
//   1. Префиксует ничего — пути приходят с /api/v1/... (как в OpenAPI).
//      Vite-proxy в dev переадресует /api/* на бэк localhost:8001;
//      в prod nginx делает то же.
//   2. Добавляет Authorization: Bearer <access>, если skipAuth не выставлен.
//   3. При 401 пытается ОДИН раз обновить пару через /api/v1/auth/refresh
//      и повторить оригинальный запрос. Параллельные 401 разделяют один
//      refresh-promise через module-level shared promise.
//   4. На !ok парсит {error: {code, message, details?}} → бросает ApiError.

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Если true — не добавлять Authorization. Для /auth/* эндпоинтов. */
  skipAuth?: boolean;
  /** JSON-тело. Если задано — сериализуется и выставляется Content-Type. */
  jsonBody?: unknown;
  /**
   * Multipart-тело (загрузка файлов, V1.4). Content-Type НЕ выставляем —
   * браузер сам ставит boundary. Идёт тем же путём, что JSON: Authorization,
   * refresh по 401, разбор envelope ошибки.
   */
  formBody?: FormData;
  /** Дополнительные заголовки (например, Idempotency-Key). */
  headers?: Record<string, string>;
}

const REFRESH_URL = '/api/v1/auth/refresh';

let refreshPromise: Promise<TokenPair> | null = null;

async function doRefresh(refreshToken: string): Promise<TokenPair> {
  const resp = await fetch(REFRESH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!resp.ok) {
    throw new ApiError('refresh_failed', 'Не удалось обновить сессию', resp.status);
  }
  const pair = (await resp.json()) as TokenPair;
  return pair;
}

async function refreshTokens(): Promise<TokenPair | null> {
  const current = authStore.getState();
  if (!current.refreshToken) return null;

  // Если уже идёт refresh — присоединяемся к нему.
  if (refreshPromise) {
    try {
      return await refreshPromise;
    } catch {
      return null;
    }
  }

  refreshPromise = doRefresh(current.refreshToken).finally(() => {
    refreshPromise = null;
  });

  try {
    const pair = await refreshPromise;
    current.setTokens(pair);
    return pair;
  } catch {
    current.logout();
    return null;
  }
}

async function parseError(resp: Response): Promise<ApiError> {
  let raw: unknown;
  try {
    raw = await resp.json();
  } catch {
    return new ApiError('http_error', `HTTP ${resp.status}`, resp.status);
  }
  if (isApiErrorEnvelope(raw)) {
    const env = raw;
    return new ApiError(env.error.code, env.error.message, resp.status, env.error.details);
  }
  return new ApiError('http_error', `HTTP ${resp.status}`, resp.status);
}

async function executeRequest(
  path: string,
  init: RequestInit,
  skipAuth: boolean,
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!skipAuth) {
    const token = authStore.getState().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(path, { ...init, headers });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth = false, jsonBody, formBody, headers: extraHeaders, ...rest } = options;

  const baseHeaders: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (jsonBody !== undefined) {
    baseHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(jsonBody);
  } else if (formBody !== undefined) {
    body = formBody;
  }

  const init: RequestInit = {
    ...rest,
    headers: { ...baseHeaders, ...(extraHeaders ?? {}) },
  };
  if (body !== undefined) init.body = body;

  let resp: Response;
  try {
    resp = await executeRequest(path, init, skipAuth);
  } catch (err) {
    throw new NetworkError('Сетевой сбой при обращении к серверу', err);
  }

  // 401 → попытка refresh + retry (один раз).
  if (resp.status === 401 && !skipAuth && path !== REFRESH_URL) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      try {
        resp = await executeRequest(path, init, skipAuth);
      } catch (err) {
        throw new NetworkError('Сетевой сбой при обращении к серверу', err);
      }
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  if (!resp.ok) {
    throw await parseError(resp);
  }

  try {
    return (await resp.json()) as T;
  } catch (err) {
    throw new NetworkError('Невалидный JSON в ответе', err);
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', jsonBody: body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', jsonBody: body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', jsonBody: body }),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

// Для тестов — даём способ форсировать reset shared refresh-promise.
export function __resetRefreshPromiseForTests(): void {
  refreshPromise = null;
}
